import {
  DataGridProProps, GridColDef, GridRenderCellParams,
  GridToolbarColumnsButton, GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import { Calendar, EventCategory, EventType } from "../../../../store";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CabDropdown } from "@CabComponents/CabDropdown";
import { PAGE_URL } from "../../../../constants";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import NoRowOverlay from "../../../EventAnalytics/NoRowsOverlay";
import { Box, Typography } from "@mui/material";
import colors from "../../../../colors";
import { createSelectOptions } from "../../../EventAnalytics/utils";
import { CabModal } from "@CabComponents/CabModal";
import { CabButton } from "@CabComponents/CabButton";
import CabDataGrid from "@CabComponents/CabDataGrid";
import { AnalyticsEventColor } from "../../../../store/cabinetApi/generated/analytics";

export type LabelInfo = { id: number | null; name: string; color?: string };
export type CalendarInfo = { id: number; name: string; color?: string };

/** Row with calendar, event_category, event_type as { id, name, color } */
export type ProcessedAnalyticsEventColor = Omit<
  AnalyticsEventColor,
  "calendar" | "event_category" | "event_type"
> & {
  calendar: CalendarInfo;
  event_category: LabelInfo;
  event_type: LabelInfo;
};

function toApiRow(row: ProcessedAnalyticsEventColor): AnalyticsEventColor & { id: number } {
  const { calendar, event_category, event_type, ...rest } = row;
  return {
    ...rest,
    id: row.id ?? 0,
    calendar: calendar.id,
    event_category: event_category?.id ?? null,
    event_type: event_type?.id ?? null,
  };
}

export type AutoAnalyticsEventCategoryLabelsProps = {
  analyticsColors: AnalyticsEventColor[];
  eventCategories: { [key: string]: EventCategory };
  eventTypes: { [key: string]: EventType };
  calendars: { [key: string]: Calendar };
  updateAnalyticColor: (color: AnalyticsEventColor & { id: number }) => void;
};

const slots: DataGridProProps["slots"] = {
  noRowsOverlay: () => (
    <NoRowOverlay emptyMessage="No Color Association Data Available" msTimeout={10000} />
  ),
  toolbar: () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row-reverse",
        width: "100%"
      }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
    </Box>
  ),
};

export const AutoAnalyticsEventCategoryLabels = ({
  analyticsColors,
  eventCategories,
  updateAnalyticColor: finalUpdateAnalyticsColor,
  calendars,
  eventTypes,
}: AutoAnalyticsEventCategoryLabelsProps) => {
  const navigate = useNavigate();
  const [pendingColor, setPendingColor] = useState<ProcessedAnalyticsEventColor | null>(null);

  const eventCategoryOptions = useMemo(
    () => createSelectOptions(Object.values(eventCategories), true, false, true),
    [eventCategories]
  );
  const eventTypeOptions = useMemo(
    () => createSelectOptions(Object.values(eventTypes), true, false, true),
    [eventTypes]
  );

  // Plain { value, label: string } for grid column valueOptions so filter/serialization never hits React nodes
  const gridCategoryValueOptions = useMemo(
    () => [
      { value: -1, label: "None" },
      ...Object.values(eventCategories)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ value: c.id, label: c.name })),
    ],
    [eventCategories]
  );
  const gridTypeValueOptions = useMemo(
    () => [
      { value: -1, label: "None" },
      ...Object.values(eventTypes)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.name })),
    ],
    [eventTypes]
  );

  const dataRows = useMemo((): ProcessedAnalyticsEventColor[] => {
    if (Object.keys(calendars).length === 0) return [];
    return analyticsColors
      .filter((row) => calendars[row.calendar])
      .map((row) => {
        const cal = calendars[row.calendar];
        const eventCat =
          row.event_category != null ? eventCategories[row.event_category] : undefined;
        const eventType = row.event_type != null ? eventTypes[row.event_type] : undefined;
        return {
          ...row,
          calendar: {
            id: row.calendar,
            name: cal?.summary ?? "",
            color: cal?.backgroundColor,
          },
          event_category: {
            id: row.event_category ?? null,
            name: eventCat?.name ?? "None",
            color: eventCat?.color,
          },
          event_type: {
            id: row.event_type ?? null,
            name: eventType?.name ?? "None",
            color: eventType?.color,
          },
        };
      });
  }, [analyticsColors, calendars, eventCategories, eventTypes]);

  const handleCategoryChange = useCallback(
    (row: ProcessedAnalyticsEventColor, newId: number) => {
      const newLabel =
        newId > 0 && eventCategories[newId]
          ? {
            id: newId,
            name: eventCategories[newId].name,
            color: eventCategories[newId].color,
          }
          : { id: null, name: "None", color: colors.white800 };
      setPendingColor({ ...row, event_category: newLabel });
    },
    [eventCategories]
  );

  const handleTypeChange = useCallback(
    (row: ProcessedAnalyticsEventColor, newId: number) => {
      const newLabel =
        newId > 0 && eventTypes[newId]
          ? {
            id: newId,
            name: eventTypes[newId].name,
            color: eventTypes[newId].color,
          }
          : { id: null, name: "None", color: colors.white800 };
      setPendingColor({ ...row, event_type: newLabel });
    },
    [eventTypes]
  );

  const columns: GridColDef<ProcessedAnalyticsEventColor>[] = useMemo(
    () => [
      {
        field: "calendar",
        headerName: "Calendar",
        type: "string",
        width: 220,
        filterable: true,
        sortable: true,
        valueGetter: (ignored, row) => row.calendar?.name ?? "",
        renderCell: ({ row }: GridRenderCellParams<ProcessedAnalyticsEventColor, ReactNode>) => (
          <CabToggleChip
            chipColor={row.calendar.color}
            label={row.calendar.name}
            sx={{ minWidth: 40 }}
            selected
          />
        ),
      },
      {
        field: "color",
        headerName: "Color / Label",
        width: 220,
        sortable: true,
        filterable: false,
        renderCell: ({
          row: { color, category },
        }: GridRenderCellParams<ProcessedAnalyticsEventColor, ReactNode>) => (
          <CabToggleChip
            chipColor={color ?? undefined}
            label={category ?? undefined}
            sx={{ minWidth: 120 }}
            selected
          />
        ),
      },
      {
        field: "event_category",
        headerName: "Category",
        type: "singleSelect",
        width: 220,
        filterable: true,
        sortable: true,
        valueGetter: (ignored, row) => row.event_category?.id ?? -1,
        valueOptions: gridCategoryValueOptions,
        renderCell: ({ row }: GridRenderCellParams<ProcessedAnalyticsEventColor, ReactNode>) => (
          <CabDropdown
            value={row.event_category?.id ?? -1}
            onChange={(e) => {
              const val = Number((e.target as { value: string | number }).value);
              if (val === -2) {
                navigate(`${PAGE_URL.SETTINGS}/analytics/org`);
                return;
              }
              handleCategoryChange(row, val);
            }}
            options={eventCategoryOptions}
            placeholder="No Category"
            sx={{ width: "100%" }}
            overrides={{
              renderValue: (value: unknown) => {
                const id = value as number;
                const info =
                  id > 0 && eventCategories[id]
                    ? eventCategories[id]
                    : { id: -1, name: "None", color: colors.white800 };
                return (
                  <CabToggleChip chipColor={info.color} label={info.name} selected />
                );
              },
              sx: {
                width: "100%",
                "& .MuiOutlinedInput-notchedOutline": { border: 0 },
              },
            }}
          />
        ),
      },
      {
        field: "event_type",
        headerName: "Type",
        type: "singleSelect",
        width: 220,
        filterable: true,
        sortable: true,
        valueGetter: (ignored, row) => row.event_type?.id ?? -1,
        valueOptions: gridTypeValueOptions,
        renderCell: ({ row }: GridRenderCellParams<ProcessedAnalyticsEventColor, ReactNode>) => (
          <CabDropdown
            value={row.event_type?.id ?? -1}
            onChange={(e) => {
              const val = Number((e.target as { value: string | number }).value);
              if (val === -2) {
                navigate(`${PAGE_URL.SETTINGS}/analytics/org`);
                return;
              }
              handleTypeChange(row, val);
            }}
            options={eventTypeOptions}
            placeholder="No Type"
            sx={{ width: "100%" }}
            overrides={{
              renderValue: (value: unknown) => {
                const id = value as number;
                const info =
                  id > 0 && eventTypes[id]
                    ? eventTypes[id]
                    : { id: -1, name: "None", color: colors.white800 };
                return (
                  <CabToggleChip chipColor={info.color} label={info.name} selected />
                );
              },
              sx: {
                width: "100%",
                "& .MuiOutlinedInput-notchedOutline": { border: 0 },
              },
            }}
          />
        ),
      },
    ],
    [
      eventCategories,
      eventCategoryOptions,
      eventTypes,
      eventTypeOptions,
      gridCategoryValueOptions,
      gridTypeValueOptions,
      handleCategoryChange,
      handleTypeChange,
      navigate,
    ]
  );

  return (
    <>
      <CabDataGrid<ProcessedAnalyticsEventColor>
        rows={dataRows}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "calendar", sort: "asc" }],
          },
        }}
        paginationMode="client"
        filterMode="client"
        sortingMode="client"
        hideFooter
        autoHeight
        disableVirtualization
        autoPageSize
        showToolbar={!!slots?.toolbar}
        slots={slots}
      />
      {pendingColor != null && (
        <CabModal
          open
          actionButtons={
            <>
              <CabButton buttonType="secondary" onClick={() => setPendingColor(null)}>
                Cancel
              </CabButton>
              <CabButton
                onClick={() => {
                  if (pendingColor.id != null) {
                    finalUpdateAnalyticsColor(toApiRow(pendingColor));
                  }
                  setPendingColor(null);
                }}
              >
                Apply
              </CabButton>
            </>
          }
        >
          <Typography>This will apply to all existing events, and future events.</Typography>
        </CabModal>
      )}
    </>
  );
};

export default AutoAnalyticsEventCategoryLabels;
